import { apiClient } from "@/services/apiClient";

const BASE = "/api/admin/sharepoint/inventory";

export type SiteScope = "SharePoint" | "OneDrive" | "All";

export interface InventoryCoverageDto {
  eligibleSites: number;
  sitesScanned: number;
  sitesNeverScanned: number;
  sitesStale: number;
  sitesRunning: number;
  sitesFailed: number;
  staleAfterDays: number;
  oldestCompletedDt?: string;
  newestCompletedDt?: string;
  siteScope: SiteScope;
}

export interface InventorySummaryDto {
  inventoriedFileCount: number;
  inventoriedBytes: number;
  librariesObserved: number;
  notModifiedSince2yCount: number;
  notModifiedSince2yBytes: number;
  notModifiedSince5yCount: number;
  notModifiedSince5yBytes: number;
  notModifiedSince7yCount: number;
  notModifiedSince7yBytes: number;
  coverage: InventoryCoverageDto;
}

export interface ExtensionSummaryDto {
  fileExtension?: string;
  fileCount: number;
  totalBytes: number;
  notModifiedSince2yCount: number;
  notModifiedSince2yBytes: number;
}

export interface AgeBucketDto {
  bucket: string;
  sortOrder: number;
  fileCount: number;
  totalBytes: number;
}

export type ScanState = "NeverScanned" | "Current" | "Stale" | "Running" | "Failed";

export interface SiteInventoryDto {
  siteCollectionId: number;
  title: string;
  url: string;
  isOneDrive: boolean;
  fileCount: number;
  totalBytes: number;
  lastCompletedCrawlDt?: string;
  lastAttemptCrawlDt?: string;
  lastAttemptStatus?: string;
  crawlAgeDays?: number;
  scanState: ScanState;
}

export interface SiteLibraryDto {
  listId: string;
  title: string;
  rootFolderUrl: string;
  webRelativeUrl: string;
  itemCount?: number;
  filesObserved: number;
  enableVersioning?: boolean;
  enableMinorVersions?: boolean;
  majorVersionLimit?: number;
  majorWithMinorVersionsLimit?: number;
  status: string;
  errorMessage?: string;
  crawlCompletedDt?: string;
}

export interface SiteInventoryDetailDto {
  site: SiteInventoryDto;
  libraries: SiteLibraryDto[];
}

export interface FileInventoryItemDto {
  sharePointFileId: number;
  siteCollectionId: number;
  siteTitle: string;
  listId: string;
  libraryTitle?: string;
  fileName: string;
  fileExtension?: string;
  serverRelativeUrl: string;
  parentFolderUrl?: string;
  fileSize?: number;
  createdDt?: string;
  modifiedDt?: string;
  authorEmail?: string;
  editorEmail?: string;
  currentVersionLabel?: string;
  hasUniquePermissions?: boolean;
  lastSeenDt: string;
}

export interface FileInventoryQuery {
  siteScope?: SiteScope;
  siteCollectionId?: number;
  listId?: string;
  extensions?: string;
  notModifiedSince?: string;
  createdBefore?: string;
  minSizeBytes?: number;
  maxSizeBytes?: number;
  search?: string;
  skip?: number;
  take?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface PagedResultDto<T> {
  items: T[];
  totalCount: number;
  skip: number;
  take: number;
  pageCount: number;
  currentPage: number;
}

export interface FilteredFileSummaryDto {
  fileCount: number;
  totalBytes: number;
  siteCount: number;
  libraryCount: number;
  notModifiedSince2yCount: number;
  notModifiedSince2yBytes: number;
  notModifiedSince5yCount: number;
  notModifiedSince5yBytes: number;
  oldestModifiedDt?: string;
  newestModifiedDt?: string;
  topExtensions: ExtensionSummaryDto[];
  ageBuckets: AgeBucketDto[];
}

export interface CrawlRunDto {
  sharePointFileCrawlId: number;
  siteCollectionId: number;
  siteTitle: string;
  siteUrl: string;
  isOneDrive: boolean;
  crawlType: string;
  status: string;
  stage: string;
  filesProcessed: number;
  librariesProcessed: number;
  siteStorageUsedMb?: number | null;
  librariesPending: number;
  startedDt?: string;
  lastCheckpointDt?: string;
  completedDt?: string;
  lastActivityDt?: string;
  errorMessage?: string;
}

export interface CrawlLibraryDto {
  sharePointLibraryId: number;
  listId: string;
  title: string;
  rootFolderUrl: string;
  itemCount?: number;
  filesProcessed: number;
  status: string;
  startedDt?: string;
  lastCheckpointDt?: string;
  completedDt?: string;
  errorMessage?: string;
}

// ---- API functions ----

export async function getInventoryCoverage(siteScope: SiteScope = "SharePoint"): Promise<InventoryCoverageDto> {
  const res = await apiClient.get<InventoryCoverageDto>(`${BASE}/coverage`, { params: { siteScope } });
  return res.data;
}

export async function getInventorySummary(siteScope: SiteScope = "SharePoint"): Promise<InventorySummaryDto> {
  const res = await apiClient.get<InventorySummaryDto>(`${BASE}/summary`, { params: { siteScope } });
  return res.data;
}

export async function getExtensionSummary(
  siteScope: SiteScope = "SharePoint",
  siteCollectionId?: number,
  take = 25,
  sortBy: 'size' | 'count' | 'name' = 'size'
): Promise<ExtensionSummaryDto[]> {
  const res = await apiClient.get<ExtensionSummaryDto[]>(`${BASE}/extensions`, {
    params: { siteScope, siteCollectionId, take, sortBy },
  });
  return res.data;
}

export async function getAgeBuckets(siteScope: SiteScope = "SharePoint", siteCollectionId?: number): Promise<AgeBucketDto[]> {
  const res = await apiClient.get<AgeBucketDto[]>(`${BASE}/age-buckets`, {
    params: { siteScope, siteCollectionId },
  });
  return res.data;
}

export async function getInventorySites(params: {
  siteScope?: SiteScope;
  search?: string;
  scanState?: string;
  skip?: number;
  take?: number;
  sortBy?: string;
  sortDirection?: string;
}): Promise<PagedResultDto<SiteInventoryDto>> {
  const res = await apiClient.get<PagedResultDto<SiteInventoryDto>>(`${BASE}/sites`, { params });
  return res.data;
}

export async function getSiteInventoryDetail(siteCollectionId: number): Promise<SiteInventoryDetailDto> {
  const res = await apiClient.get<SiteInventoryDetailDto>(`${BASE}/sites/${siteCollectionId}`);
  return res.data;
}

export async function getInventoryFiles(query: FileInventoryQuery): Promise<PagedResultDto<FileInventoryItemDto>> {
  const res = await apiClient.get<PagedResultDto<FileInventoryItemDto>>(`${BASE}/files`, { params: query });
  return res.data;
}

export async function getInventoryFilesSummary(query: FileInventoryQuery): Promise<FilteredFileSummaryDto> {
  const res = await apiClient.get<FilteredFileSummaryDto>(`${BASE}/files/summary`, { params: query });
  return res.data;
}

export async function exportInventoryFiles(query: FileInventoryQuery): Promise<Blob> {
  const res = await apiClient.get(`${BASE}/files/export`, { params: query, responseType: "blob" });
  return res.data as Blob;
}

export async function getCrawlRuns(params: {
  search?: string;
  siteCollectionId?: number;
  status?: string;
  siteScope?: SiteScope;
  skip?: number;
  take?: number;
  sortBy?: string;
  sortDirection?: string;
}): Promise<PagedResultDto<CrawlRunDto>> {
  const res = await apiClient.get<PagedResultDto<CrawlRunDto>>(`${BASE}/crawls`, { params });
  return res.data;
}

export async function getCrawlLibraries(crawlId: number): Promise<CrawlLibraryDto[]> {
  const res = await apiClient.get<CrawlLibraryDto[]>(`${BASE}/crawls/${crawlId}/libraries`);
  return res.data;
}

// ---- Data sources for the shared file explorer ----

export interface FileInventoryDataSource {
  getExtensions(query: Pick<FileInventoryQuery, "siteScope" | "siteCollectionId">): Promise<ExtensionSummaryDto[]>;
  getFiles(query: FileInventoryQuery): Promise<PagedResultDto<FileInventoryItemDto>>;
  getFilesSummary(query: FileInventoryQuery): Promise<FilteredFileSummaryDto>;
  exportFiles(query: FileInventoryQuery): Promise<Blob>;
}

export const adminFileInventoryDataSource: FileInventoryDataSource = {
  getExtensions: ({ siteScope, siteCollectionId }) => getExtensionSummary(siteScope, siteCollectionId, 100_000, "name"),
  getFiles: getInventoryFiles,
  getFilesSummary: getInventoryFilesSummary,
  exportFiles: exportInventoryFiles,
};

// Resource-scoped endpoints: the backend resolves the site from the group and ignores any site/scope sent here.
const resourceBase = (groupId: string) => `/api/resources/graph/${encodeURIComponent(groupId)}/file-inventory`;

export async function getResourceSiteInventory(groupId: string): Promise<SiteInventoryDetailDto> {
  const res = await apiClient.get<SiteInventoryDetailDto>(resourceBase(groupId));
  return res.data;
}

export function createResourceFileInventoryDataSource(groupId: string): FileInventoryDataSource {
  const base = resourceBase(groupId);
  return {
    getExtensions: async () =>
      (await apiClient.get<ExtensionSummaryDto[]>(`${base}/extensions`, { params: { take: 100_000, sortBy: "name" } })).data,
    getFiles: async (query) =>
      (await apiClient.get<PagedResultDto<FileInventoryItemDto>>(`${base}/files`, { params: query })).data,
    getFilesSummary: async (query) =>
      (await apiClient.get<FilteredFileSummaryDto>(`${base}/files/summary`, { params: query })).data,
    exportFiles: async (query) =>
      (await apiClient.get(`${base}/files/export`, { params: query, responseType: "blob" })).data as Blob,
  };
}
